import { Link } from 'react-router-dom';
import { BrandLogo } from '@/components/ui/logo';
import { useTheme } from '@/hooks/useTheme';

interface FooterLinkGroup {
  title: string;
  links: { label: string; href: string; external?: boolean }[];
}

const linkGroups: FooterLinkGroup[] = [
  {
    title: 'Product',
    links: [
      { label: 'Pricing', href: '/pricing' },
      { label: 'Integrations', href: '/integrations' },
      { label: 'Documentation', href: '/docs' },
      { label: 'API Reference', href: '/docs/api' },
      { label: 'Changelog', href: '/changelog' },
      { label: 'System Status', href: '/status' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Refund Policy', href: '/refunds' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'Contact', href: '/contact' },
      {
        label: 'support@hooklink.net',
        href: 'mailto:support@hooklink.net',
        external: true,
      },
    ],
  },
];

export function PublicFooter() {
  const theme = useTheme().resolvedTheme;
  return (
    <footer className="border-t border-border/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main footer content */}
        <div className="py-10 sm:py-12 flex flex-col sm:flex-row gap-8">
          {/* Brand column */}
          <div className="sm:mr-auto">
            <BrandLogo
              size={28}
              textClassName="font-display text-2xl font-bold tracking-[-0.01em]"
            />
            <p className="text-xs text-muted-foreground/80 leading-relaxed max-w-[200px]">
              Webhooks to localhost in under 50ms. Zero infrastructure.
            </p>
            <p className="text-[11px] text-muted-foreground/80 mt-1.5 max-w-[200px]">
              Built by a developer who wanted webhook testing to be simple.
            </p>
            <p className="flex flex-row gap-2 mt-8">
              <a
                href="https://www.producthunt.com/products/hooklink/reviews/new?utm_source=badge-product_review&utm_medium=badge&utm_source=badge-hooklink"
                target="_blank"
              >
                <img
                  src={`https://api.producthunt.com/widgets/embed-image/v1/product_review.svg?product_id=1168586&theme=${theme === 'dark' ? 'dark' : 'light'}`}
                  alt="Hooklink - Your&#0032;localhost&#0044;&#0032;reachable&#0032;from&#0032;anywhere | Product Hunt"
                  style={{ width: '250px', height: '54px' }}
                  width="250"
                  height="54"
                />
              </a>
              <a
                href="https://www.producthunt.com/products/hooklink?utm_source=badge-follow&utm_medium=badge&utm_source=badge-hooklink"
                target="_blank"
              >
                <img
                  src={`https://api.producthunt.com/widgets/embed-image/v1/follow.svg?product_id=1168586&theme=${theme === 'dark' ? 'dark' : 'light'}`}
                  alt="Hooklink - Your&#0032;localhost&#0044;&#0032;reachable&#0032;from&#0032;anywhere | Product Hunt"
                  style={{ width: '250px', height: '54px' }}
                  width="250"
                  height="54"
                />
              </a>
              <a
                href="https://www.producthunt.com/products/hooklink?embed=true&amp;utm_source=badge-featured&amp;utm_medium=badge&amp;utm_campaign=badge-hooklink"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  alt="Hooklink - Your localhost, reachable from anywhere | Product Hunt"
                  width="250"
                  height="54"
                  style={{ width: '250px', height: '54px' }}
                  src={`https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1083949&theme=${theme === 'dark' ? 'dark' : 'light'}&t=1772281007806`}
                />
              </a>
            </p>
          </div>

          {/* Link groups */}
          {linkGroups.map((group) => (
            <div key={group.title} className="sm:min-w-[140px]">
              <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
                {group.title}
              </h3>
              <ul className="space-y-2">
                {group.links.map((link) => (
                  <li key={link.href}>
                    {link.external ? (
                      <a
                        href={link.href}
                        className="text-xs text-muted-foreground/80 hover:text-foreground transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-xs text-muted-foreground/80 hover:text-foreground transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border/30 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span className="text-[11px] text-muted-foreground/80">
            &copy; {new Date().getFullYear()} Hooklink. All rights reserved.
          </span>
          <span className="text-[11px] text-muted-foreground/80">
            v{__APP_VERSION__}
          </span>
        </div>
      </div>
    </footer>
  );
}
